import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';

import SvgCrederaLogoName from '../svg-components/svg-credera-logo-name';
import compass from '../assets/global-home-page/compass.svg';
import airplane from '../assets/global-home-page/airplane.gif';
import HMFlag from '../assets/icons/flags/round/australia.svg';
import DEFlag from '../assets/icons/flags/round/germany.svg';
import GBFlag from '../assets/icons/flags/round/uk.svg';
import USFlag from '../assets/icons/flags/round/us.svg';
import INFlag from '../assets/icons/flags/round/india.svg';
import NZFlag from '../assets/icons/flags/round/new-zealand.svg';
import locationsList from '../static-content/locale-menu.json';
import CONTENT from '../static-content/global-home-page.json';

import Layout from '../components/layout';
import { animation, Container, TitleSet, colors, Text, Button, media } from '../ui-components';
import updateLocationUrl from '../utils/update-user-locale';
import { ArrowRight } from 'react-feather';
import GlobalPageFooter from '../components/global-page-footer';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

const propTypes = {
  data: PropTypes.shape({
    cloud: PropTypes.object
  })
};

const IndexPage = props => {
  const { data } = props;
  const cloud = getImage(data?.cloud);
  const [selectedLocale, setSelectedLocale] = useState(locationsList.locations[0]);

  const changeLocale = () => {
    if (selectedLocale) {
      updateLocationUrl(selectedLocale);
    }
  };

  const getFlagImage = flagName => {
    switch (flagName) {
      case 'USFlag':
        return <CountryFlag src={USFlag} alt="USA Country Flag" />;
      case 'DEFlag':
        return <CountryFlag src={DEFlag} alt="Germany Country Flag" />;
      case 'GBFlag':
        return <CountryFlag src={GBFlag} alt="Great Britain Country Flag" />;
      case 'HMFlag':
        return <CountryFlag src={HMFlag} alt="Australia Country Flag" />;
      case 'INFlag':
        return <CountryFlag src={INFlag} alt="India Country Flag" />;
      case 'NZFlag':
        return <CountryFlag src={NZFlag} alt="New Zealand Country Flag" />;
      default:
        return null;
    }
  };

  /*
   * EB: We tried to include the SEO component, but the static query broke the page in production.
   * Not super clear why this happened, but to avoid it, we recreated a simplified version of it here.
   */
  const staticSEOObject = () => {
    const lang = 'en';
    const title = 'Global Home';
    const fullTitle = 'Global Home | Credera';
    const description =
      'Credera, a global boutique consulting firm, combines transformational consulting capabilities, deep industry knowledge, and AI and technology expertise to deliver valuable customer experiences and accelerated growth across a broad range of industries worldwide.';
    const image = 'https://www.credera.com/share.png';

    return (
      <Helmet
        htmlAttributes={{ lang }}
        title={title}
        titleTemplate={fullTitle}
        meta={[
          {
            name: 'description',
            content: description
          },
          {
            property: 'og:title',
            content: fullTitle
          },
          {
            property: 'og:description',
            content: description
          },
          {
            property: 'og:site_name',
            content: 'Credera'
          },
          {
            property: 'og:type',
            content: 'website'
          },
          {
            property: 'og:url',
            content: `https://www.credera.com`
          },
          {
            property: 'og:image',
            content: image
          },
          {
            name: 'twitter:card',
            content: 'summary_large_image'
          },
          {
            name: 'twitter:creator',
            content: '@credera'
          },
          {
            name: 'twitter:title',
            content: fullTitle
          },
          {
            name: 'twitter:description',
            content: description
          },
          {
            property: 'twitter:image',
            content: image
          }
        ]}
      />
    );
  };

  return (
    <Layout globalPage>
      {staticSEOObject()}
      <PageWrapper full>
        <ImageWrapper>
          <Compass src={compass} alt="man sitting on compass" />
          <Cloud1 loading="eager" fetchpriority="high" objectFit="contain" image={cloud} alt="cloud" />
          <Cloud2 loading="eager" fetchpriority="high" objectFit="contain" image={cloud} alt="cloud" />
          <Cloud3 loading="eager" fetchpriority="high" objectFit="contain" image={cloud} alt="cloud" />
          <AirplaneGif src={airplane} alt="woman riding paper airplane" />
        </ImageWrapper>
        <PageContainer>
          <HeroContainer>
            <LogoContainer>
              <SvgCrederaLogoName />
            </LogoContainer>
            <TitleSet
              title={CONTENT.TAGLINE}
              bodyText={CONTENT.TAGLINE__SUBTITLE}
              underlinedText={CONTENT.TAGLINE__UNDERLINE}
              underlineWidth={200}
            />
          </HeroContainer>
          <RegionSelectorContainer>
            <Text size={'small'}>{'Select a country below.'}</Text>
            <RegionSelector>
              {locationsList.locations.map(
                locale =>
                  locale.location != 'Global' && (
                    <RegionSelectionButton
                      key={locale.location}
                      onClick={() => setSelectedLocale(locale)}
                      aria-label={`Set your location to ${locale.location}`}
                      selected={locale === selectedLocale}
                    >
                      {getFlagImage(locale.countryFlag)}
                      <Text>{locale.location}</Text>
                    </RegionSelectionButton>
                  )
              )}
              <RegionConfirmationButton onClick={() => changeLocale()}>
                {'Go to regional page'} <ArrowRight size={20} />
              </RegionConfirmationButton>
            </RegionSelector>
          </RegionSelectorContainer>
        </PageContainer>
        <GlobalPageFooter />
      </PageWrapper>
    </Layout>
  );
};

IndexPage.propTypes = propTypes;

export default IndexPage;

export const pageQuery = graphql`
  query GlobalIndexPageQuery {
    cloud: file(relativePath: { eq: "photography/home/cloud.png" }) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, width: 600)
      }
    }
  }
`;

const PageWrapper = styled.div`
  background: linear-gradient(180deg, ${colors.secondaryBackground} 74.22%, ${colors.winterBlue} 195.82%);
  max-width: none;
  height: 100vh;
  min-height: 1040px;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  @media screen and (max-width: ${media.mediumBottom}) {
    min-height: 952px;
  }
`;

const PageContainer = styled(Container)`
  padding: 0;
  margin-top: 120px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: center;
  text-align: center;

  @media screen and (max-width: ${media.mediumBottom}) {
    margin-top: 60px;
    padding: 0 20px;
    gap: 40px;
  }

  @media screen and (min-width: ${media.hugeBottom}) and (min-height: ${media.largeTop}) {
    margin: auto;
  }
`;

const HeroContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  z-index: 10;
`;

const LogoContainer = styled.div`
  width: 288px;

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 180px;
  }
`;

const RegionSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 10;
`;

const RegionSelector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 400px;
  border-radius: 20px;
  background-color: ${colors.pureWhite};
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${media.mediumBottom}) {
    min-width: 335px;
  }
`;

const RegionSelectionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 335px;
  padding: 12px 16px;
  height: 56px;
  background-color: ${({ selected }) => (selected ? `${colors.secondaryBackground}` : `${colors.pureWhite}`)};
  text-transform: none;

  &:hover {
    background-color: ${({ selected }) => (selected ? `${colors.secondaryBackground}` : `${colors.gray5}`)};
  }

  &:first-of-type {
    border-radius: 20px 20px 0 0;
  }
`;

const CountryFlag = styled.img`
  margin: 0;
  width: 30px;
  height: 30px;
`;

const RegionConfirmationButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 20px 20px;
  height: 56px;
  padding: 12px 16px;
  font-size: 16px;
  gap: 4px;

  svg {
    transition: transform ${animation.general.durationAndTiming};
  }

  // Source: https://stackoverflow.com/questions/6542212/use-css3-transitions-with-gradient-backgrounds/63848864#63848864
  @property --bgColor1 {
    syntax: '<color>';
    initial-value: ${colors.oceanBlue};
    inherits: false;
  }

  @property --bgColor2 {
    syntax: '<color>';
    initial-value: ${colors.oceanBlue};
    inherits: false;
  }

  background: linear-gradient(90deg, var(--bgColor1) 12.75%, var(--bgColor2) 91.13%);
  transition: --bgColor1 500ms, --bgColor2 500ms;

  &:hover {
    --bgColor1: ${colors.linkHover};
    --bgColor2: ${colors.linkHover2};

    svg {
      transform: translateX(4px);
    }
  }
`;

const ImageWrapper = styled.div`
  position: absolute;
  width: 100%;
  max-width: ${media.hugeBottom};
  height: 1040px;
  pointer-events: none;

  @media screen and (min-width: ${media.hugeBottom}) {
    left: calc((100vw - ${media.hugeBottom}) / 2);
  }

  @media screen and (min-width: ${media.hugeBottom}) and (min-height: ${media.largeTop}) {
    top: calc((100vh - 1040px) / 2);
  }
`;

const Compass = styled.img`
  margin: 0;
  width: 221.131px;
  height: 252px;
  position: absolute;
  z-index: 1;
  top: 312px;
  left: 51.1px; // starting animation position

  @keyframes compassSlideRight {
    100% {
      transform: translateX(68px);
    }
  }
  /* animation shorthand: duration | timing-function | delay | fill-mode | name */
  /* fill-mode forwards causes the target to retain the computed values set by the final keyframe */
  animation: 1400ms ease-out 400ms forwards compassSlideRight;

  @media screen and (max-width: ${media.mediumBottom}) {
    display: none;
  }
`;

const Cloud1 = styled(GatsbyImage)`
  margin: 0;
  width: 342.323px;
  height: 186px;
  position: absolute;
  z-index: 2;
  top: 422px;
  left: -93px;

  @keyframes cloud1SlideRight {
    100% {
      transform: translateX(68px);
    }
  }
  animation: 1400ms ease-out 400ms forwards cloud1SlideRight;

  @media screen and (max-width: ${media.mediumBottom}) {
    // Referenced as cloud4 in Figma
    width: 110px;
    height: 58px;
    top: 230px;
    left: 17px;
    animation: none;
  }
`;

const Cloud2 = styled(GatsbyImage)`
  margin: 0;
  width: 399.974px;
  height: 216px;
  position: absolute;
  z-index: 1;
  top: 274px;
  right: -110px;

  @keyframes cloud2SlideLeft {
    100% {
      transform: translateX(-86px);
    }
  }
  animation: 1400ms ease-out 400ms forwards cloud2SlideLeft;

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 346px;
    height: 187px;
    top: 661px;
    right: unset;
    left: -219px;

    @keyframes cloud2SlideLeft {
      100% {
        transform: translateX(59px);
      }
    }
  }
`;

const Cloud3 = styled(GatsbyImage)`
  margin: 0;
  width: 202.63px;
  height: 108px;
  position: absolute;
  z-index: 1;
  top: -24px;
  left: 177px;
  transform: scaleX(-1);

  @keyframes cloud3SlideRight {
    100% {
      transform: translateX(90px) scaleX(-1);
    }
  }
  animation: 1400ms ease-out 400ms forwards cloud3SlideRight;

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 222px;
    height: 118px;
    top: -64px;
    left: unset;
    right: -164px;
    transform: scaleX(1);

    @keyframes cloud3SlideLeft {
      100% {
        transform: translateX(-66px);
      }
    }
    animation: 1400ms ease-out 400ms forwards cloud3SlideLeft;
  }
`;

const AirplaneGif = styled.img`
  margin: 0;
  width: 535px;
  position: absolute;
  right: -92px;
  bottom: -72px;

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 290px;
    right: -81px;
    bottom: 108px;
  }
`;
